import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getProductBySlugApi, getProductsApi } from '../../api/productApi';
import { addToCart } from '../../store/slices/cartSlice';
import imgUrl from '../../utils/imgUrl';
import useIsMobile from '../../hooks/useIsMobile';

/* Parse variant attributes safely */
function parseAttrs(v) {
  if (!v?.attributes) return {};
  return typeof v.attributes === 'string' ? JSON.parse(v.attributes) : v.attributes;
}

const REVIEWS = [
  { id:1, name:'Arjun Bose',      loc:'Kolkata',   rating:5, title:'Exactly what Bengali fashion needed', text:'The quality of the fabric is outstanding. Wore it to Durga Puja and got so many compliments. The embroidery is crisp and the fit is perfect for my build.', date:'12 Oct 2024', helpful:34, verified:true },
  { id:2, name:'Priya Mukherjee', loc:'Mumbai',    rating:5, title:'Finally, something to be proud of',   text:"I'm so happy this brand exists. The Howrah Bridge design brings back so many memories of home. Excellent stitching, ships fast.", date:'8 Nov 2024', helpful:27, verified:true },
  { id:3, name:'Debashis Roy',    loc:'Bangalore', rating:4, title:'Great quality, slightly long delivery', text:'Product itself is 5/5 but took a week extra to arrive. The weave is genuinely premium — nothing like the fast-fashion alternatives. Will order again.', date:'23 Nov 2024', helpful:19, verified:true },
  { id:4, name:'Suchitra Das',    loc:'Kolkata',   rating:5, title:'Perfect Puja gift',  text:'Bought as a gift for my father. He absolutely loved it. The packaging itself is beautiful — very gift-worthy.', date:'14 Dec 2024', helpful:41, verified:true },
];

export default function ProductDetail() {
  const { slug }   = useParams();
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { user }   = useSelector((s) => s.auth);
  const isMobile   = useIsMobile();

  const [product, setProduct]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [related, setRelated]       = useState([]);
  const [activeImg, setActiveImg]   = useState(0);
  const [zoomed, setZoomed]         = useState(false);
  const [selections, setSelections] = useState({}); // { attrName: value }
  const [qty, setQty]               = useState(1);
  const [selError, setSelError]     = useState(false);
  const [addedAnim, setAddedAnim]   = useState(false);
  const [showSG, setShowSG]         = useState(false);
  const [showBar, setShowBar]       = useState(false);
  const [pincode, setPincode]       = useState('');
  const [pinResult, setPinResult]   = useState(null);
  const [accOpen, setAccOpen]       = useState({ details:false, care:false, ship:false, promise:false });
  const atcRef = useRef(null);

  /* load product */
  useEffect(() => {
    setLoading(true);
    setSelections({}); setQty(1); setSelError(false);
    getProductBySlugApi(slug)
      .then((r) => {
        setProduct(r.data);
        setActiveImg(0);
        /* auto-select first available value for each attribute */
        const schema = Array.isArray(r.data.variant_attributes) ? r.data.variant_attributes : [];
        const vlist  = r.data.variants || [];
        if (schema.length && vlist.length) {
          const init = {};
          schema.forEach((attr) => {
            const first = vlist.find((v) => parseAttrs(v)[attr.name]);
            if (first) init[attr.name] = parseAttrs(first)[attr.name];
          });
          setSelections(init);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  /* load related */
  useEffect(() => {
    if (!product?.category_id) return;
    getProductsApi({ category: product.category_id, limit: 5 })
      .then((r) => setRelated((r.data.products || []).filter((p) => p.id !== product.id).slice(0, 4)))
      .catch(() => {});
  }, [product]);

  /* sticky bar */
  useEffect(() => {
    const onScroll = () => {
      if (!atcRef.current) return;
      setShowBar(atcRef.current.getBoundingClientRect().bottom < 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* dynamic variant helpers */
  const schema   = Array.isArray(product?.variant_attributes) ? product.variant_attributes : [];
  const variants = product?.variants || [];

  /* all unique values for one attribute (optionally filtered by other current selections) */
  const uniqueVals = (attrName) => {
    const others = Object.fromEntries(Object.entries(selections).filter(([k]) => k !== attrName));
    const pool = variants.filter((v) =>
      Object.entries(others).every(([k, val]) => val === '' || parseAttrs(v)[k] === val)
    );
    return [...new Set(pool.map((v) => parseAttrs(v)[attrName]).filter(Boolean))];
  };

  /* is a specific value OOS given everything else currently selected? */
  const isValOOS = (attrName, value) => {
    const merged = { ...selections, [attrName]: value };
    const matching = variants.filter((v) =>
      Object.entries(merged).every(([k, val]) => val === '' || parseAttrs(v)[k] === val)
    );
    return matching.length > 0 && matching.every((v) => v.stock === 0);
  };

  /* find the fully-selected variant */
  const selectedVariant = schema.length
    ? variants.find((v) => schema.every((a) => parseAttrs(v)[a.name] === selections[a.name]))
    : null;
  const stockLeft = selectedVariant?.stock ?? (variants.length === 0 ? null : null);

  /* select one attribute value */
  const selectVal = (attrName, value) => {
    setSelections((s) => ({ ...s, [attrName]: value }));
    setSelError(false);
  };

  /* is the size guide relevant (any size-type attribute in schema)? */
  /* pincode */
  const checkPin = () => {
    if (pincode.length !== 6) return;
    const n = parseInt(pincode, 10);
    if (n >= 700000 && n <= 799999)      setPinResult('Same-day delivery available in Kolkata!');
    else if (n >= 600000 && n <= 699999) setPinResult('Delivery in 2–3 business days.');
    else                                  setPinResult('Delivery in 4–7 business days.');
  };

  /* add to cart */
  const handleATC = useCallback(async () => {
    if (schema.length > 0 && !selectedVariant) { setSelError(true); return; }
    if (!user) { navigate('/login'); return; }
    setSelError(false);
    dispatch(addToCart({ product_id: product.id, quantity: qty, variant_id: selectedVariant?.id || null }));
    setAddedAnim(true);
    setTimeout(() => setAddedAnim(false), 1400);
  }, [user, product, qty, selectedVariant, schema, dispatch, navigate]);

  const handleBuyNow = async () => { await handleATC(); navigate('/checkout'); };

  const images  = product?.images || [];
  const mainImg = images[activeImg]?.image_url;
  const imgSrc  = imgUrl;
  const discPct = (product?.compare_price && product?.price) ? Math.round((1 - product.price / product.compare_price) * 100) : 0;
  const toggleAcc = (k) => setAccOpen((o) => ({ ...o, [k]: !o[k] }));
  const specs = (() => { try { return typeof product?.specifications === 'string' ? JSON.parse(product.specifications) : (product?.specifications || {}); } catch { return {}; } })();

  if (loading) return <div style={S.loadWrap}><div style={S.spinner} /></div>;
  if (!product) return <div style={S.loadWrap}><p style={{ color:'#888' }}>Product not found.</p></div>;

  return (
    <>
      {/* Size Guide Modal */}
      {showSG && (
        <div style={S.sgBack} onClick={(e) => e.target === e.currentTarget && setShowSG(false)}>
          <div style={S.sgModal}>
            <button style={S.sgClose} onClick={() => setShowSG(false)}>✕</button>
            <p style={S.sgTitle}>SIZE GUIDE</p>
            <p style={{ fontSize:'13px', color:'#888', marginBottom:'16px', lineHeight:1.6 }}>
              Measurements in inches. For a relaxed fit, size up one.
            </p>
            <table style={S.sgTable}>
              <thead>
                <tr>{['Size','Chest','Length','Shoulder','Sleeve'].map((h) => <th key={h} style={S.sgTh}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {[['XS','34–36"','27"','16.5"','8"'],['S','36–38"','28"','17.5"','8.5"'],['M','38–40"','29"','18.5"','9"'],
                  ['L','40–42"','30"','19.5"','9.5"'],['XL','42–44"','31"','20.5"','10"'],['XXL','44–46"','32"','21.5"','10.5"']
                ].map((row) => (
                  <tr key={row[0]}>{row.map((c, i) => <td key={i} style={S.sgTd}>{c}</td>)}</tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize:'11px', color:'#888', marginTop:'14px', lineHeight:1.6 }}>
              ⚡ Cold wash recommended. May shrink slightly after first wash — size up if in doubt.
            </p>
          </div>
        </div>
      )}

      {/* Zoom Modal */}
      {zoomed && mainImg && (
        <div style={S.zoomOv} onClick={() => setZoomed(false)}>
          <button style={S.zoomClose} onClick={() => setZoomed(false)}>✕</button>
          <img src={imgSrc(mainImg)} alt="" style={{ maxHeight:'90vh', maxWidth:'90vw', objectFit:'contain' }} />
        </div>
      )}

      <div className="page-top">
        {/* Breadcrumb */}
        <div style={{ ...S.bc, padding: isMobile ? '14px 18px' : '18px 60px' }}>
          <Link to="/" style={S.bcLink}>Home</Link><span style={S.bcSep}>›</span>
          <Link to="/products" style={S.bcLink}>Products</Link>
          {product.category_name && <><span style={S.bcSep}>›</span><Link to={`/products?category=${product.category_id}`} style={S.bcLink}>{product.category_name}</Link></>}
          <span style={S.bcSep}>›</span><span style={S.bcCur}>{product.name}</span>
        </div>

        {/* PDP 2-col grid */}
        <div style={{ ...S.pdpWrap, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', padding: isMobile ? '0 18px' : '0 60px' }}>
          {/* ── Gallery ── */}
          <div style={{ ...S.galleryWrap, position: isMobile ? 'relative' : 'sticky', height: isMobile ? 'auto' : 'calc(100vh - var(--ticker-h) - var(--nav-h))', flexDirection: isMobile ? 'column' : 'row' }}>
            {/* Thumbs rail */}
            <div style={{ ...S.thumbsRail, flexDirection: isMobile ? 'row' : 'column', width: isMobile ? '100%' : '70px', overflowX: isMobile ? 'auto' : undefined, overflowY: isMobile ? 'hidden' : 'auto', order: isMobile ? 2 : undefined }}>
              {(images.length > 0 ? images : [{id:'ph0'},{id:'ph1'},{id:'ph2'}]).map((img, i) => (
                <div key={img.id} style={{ ...S.thumb, borderColor: activeImg === i ? 'var(--red)' : 'transparent' }}
                  onClick={() => setActiveImg(i)}>
                  {imgSrc(img.image_url)
                    ? <img src={imgSrc(img.image_url)} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    : <span style={{ fontSize:'18px' }}>👕</span>}
                </div>
              ))}
            </div>

            {/* Main image box */}
            <div style={S.gMain} onClick={() => mainImg && setZoomed(true)}>
              {product.is_featured && <span style={S.gTag}>FEATURED</span>}
              {discPct > 0 && (
                <span style={{ ...S.gTag, left:'auto', right:'14px', background:'var(--green)' }}>{discPct}% OFF</span>
              )}

              <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {imgSrc(mainImg)
                  ? <img src={imgSrc(mainImg)} alt={product.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  : <span style={{ fontSize:'80px', opacity:.15 }}>👕</span>}
              </div>

              {images.length > 1 && (
                <div style={S.gArrows}>
                  <button style={S.gArrow} onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i - 1 + images.length) % images.length); }}>‹</button>
                  <button style={S.gArrow} onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i + 1) % images.length); }}>›</button>
                </div>
              )}
            </div>
          </div>

          {/* ── Info Panel ── */}
          <div style={{ ...S.infoPanel, padding: isMobile ? '20px 0 40px' : '24px 0 80px 44px', maxHeight: isMobile ? 'none' : 'calc(100vh - var(--ticker-h) - var(--nav-h))', overflowY: isMobile ? 'visible' : 'auto' }}>
            {/* Badges */}
            <div style={{ display:'flex', gap:'7px', marginBottom:'14px', flexWrap:'wrap' }}>
              {product.is_featured && <span style={{ ...S.pb, background:'var(--gold)', color:'#111' }}>FEATURED</span>}
              {product.brand && <span style={{ ...S.pb, background:'var(--black)', color:'#fff' }}>{product.brand}</span>}
              {discPct > 0 && <span style={{ ...S.pb, background:'var(--red)', color:'#fff' }}>{discPct}% OFF</span>}
            </div>

            <h1 style={S.pdpName}>{product.name}</h1>
            {product.brand && <p style={{ fontFamily:'var(--font-script)', fontSize:'18px', color:'#888', marginBottom:'14px' }}>by {product.brand}</p>}

            {/* Rating row */}
            <div style={S.ratingRow}>
              <span style={{ color:'var(--gold)', fontSize:'16px', letterSpacing:'2px' }}>★★★★★</span>
              <span style={{ fontSize:'13px', color:'#888' }}>4.8</span>
              <span style={{ color:'var(--border)', margin:'0 2px' }}>|</span>
              <span style={{ fontSize:'12px', color:'var(--red)', fontWeight:600, cursor:'pointer', textDecoration:'underline' }}>
                {REVIEWS.length} Reviews
              </span>
              <span style={{ color:'var(--border)', margin:'0 2px' }}>|</span>
              <span style={{ fontSize:'12px', color:'var(--charcoal)', fontWeight:600 }}>
                <span style={{ color:'var(--red)' }}>127</span> sold
              </span>
              <span style={{ color:'var(--border)', margin:'0 2px' }}>|</span>
              <span style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'11px', color:'var(--green)', fontWeight:600 }}>
                <span style={S.liveDot} />23 viewing
              </span>
            </div>

            {/* Price block */}
            <div style={{ marginBottom:'22px' }}>
              <div style={{ display:'flex', alignItems:'baseline', gap:'10px' }}>
                <span style={{ fontSize:'30px', fontWeight:700, color: discPct > 0 ? 'var(--red)' : 'var(--black)' }}>
                  ₹{Math.round((product.price || 0) + (selectedVariant?.price_adjustment || 0)).toLocaleString('en-IN')}
                </span>
                {product.compare_price && (
                  <span style={{ fontSize:'17px', color:'#bbb', textDecoration:'line-through', fontWeight:400 }}>
                    ₹{Number(product.compare_price).toLocaleString('en-IN')}
                  </span>
                )}
                {discPct > 0 && (
                  <span style={{ fontSize:'12px', background:'var(--gold)', color:'#111', padding:'3px 10px', fontWeight:700, alignSelf:'center' }}>
                    {discPct}% OFF
                  </span>
                )}
              </div>
              <p style={{ fontSize:'12px', color:'#888', marginTop:'5px' }}>
                Or <strong style={{ color:'var(--black)' }}>₹{Math.round((product.price || 0) / 3).toLocaleString('en-IN')}/mo</strong> × 3 no-cost EMI
              </p>
            </div>

            {/* Dynamic Attribute Pickers */}
            {schema.map((attr) => {
              const vals    = uniqueVals(attr.name);
              const selVal  = selections[attr.name] || '';

              if (attr.type === 'color') {
                /* Color swatches */
                const swatches = [...new Map(
                  vals.map((hex) => {
                    const v = variants.find((vv) => parseAttrs(vv)[attr.name] === hex);
                    const label = v ? (parseAttrs(v)[`${attr.name}_label`] || hex) : hex;
                    return [hex, { hex, label }];
                  })
                ).values()];
                return (
                  <div key={attr.name} style={{ marginBottom:'22px' }}>
                    <p style={S.pickerLabel}>
                      {(attr.label || attr.name).toUpperCase()}:&nbsp;
                      <strong style={{ color:'var(--black)' }}>
                        {selVal ? (swatches.find((s) => s.hex === selVal)?.label || selVal) : '—'}
                      </strong>
                    </p>
                    <div style={{ display:'flex', gap:'9px', flexWrap:'wrap' }}>
                      {swatches.map(({ hex, label }) => {
                        const oos = isValOOS(attr.name, hex);
                        return (
                          <div key={hex} title={label}
                            style={{ ...S.cOpt, background: hex, borderColor: selVal === hex ? 'var(--black)' : 'transparent', transform: selVal === hex ? 'scale(1.1)' : 'scale(1)', outline: hex === '#ffffff' || hex === '#FFFFFF' ? '1px solid #ccc' : 'none', outlineOffset:'2px', opacity: oos ? .3 : 1, cursor: oos ? 'not-allowed' : 'pointer' }}
                            onClick={() => !oos && selectVal(attr.name, hex)}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              }

              /* Size / Select / Text / Number → button chips */
              const options = attr.options?.length ? attr.options : vals;
              if (!options.length) return null;
              return (
                <div key={attr.name} style={{ marginBottom:'10px' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'9px' }}>
                    <p style={S.pickerLabel}>
                      {(attr.label || attr.name).toUpperCase()}:&nbsp;
                      <strong style={{ color:'var(--black)' }}>{selVal || 'Select'}</strong>
                    </p>
                    {attr.type === 'size' && (
                      <button style={S.sgLink} onClick={() => setShowSG(true)}>Size Guide</button>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:'7px', flexWrap:'wrap', marginBottom:'4px' }}>
                    {options.map((opt) => {
                      const oos = isValOOS(attr.name, opt);
                      return (
                        <button key={opt} disabled={oos}
                          onClick={() => !oos && selectVal(attr.name, opt)}
                          style={{ ...S.szOpt, ...(selVal === opt ? S.szOptSel : {}), ...(oos ? S.szOptOOS : {}) }}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {selError && <p style={{ fontSize:'12px', color:'var(--red)', fontWeight:600, marginBottom:'8px' }}>Please select all options</p>}

            {/* Stock indicator */}
            {stockLeft !== null && (
              <div style={{ display:'flex', alignItems:'center', gap:'8px', margin:'12px 0 18px' }}>
                {stockLeft === 0 ? (
                  <><span style={{ ...S.stockDot, background:'var(--red)' }} /><span style={{ fontSize:'12px', fontWeight:600, color:'var(--red)' }}>Out of stock</span></>
                ) : stockLeft < 10 ? (
                  <><span style={{ ...S.stockDot, background:'#F59E0B', animation:'livePulse 1.5s infinite' }} /><span style={{ fontSize:'12px', fontWeight:600, color:'#F59E0B' }}>Only {stockLeft} left — order soon!</span></>
                ) : (
                  <><span style={{ ...S.stockDot, background:'var(--green)' }} /><span style={{ fontSize:'12px', fontWeight:600, color:'var(--green)' }}>In stock</span></>
                )}
              </div>
            )}

            {/* Pincode */}
            <div style={S.pincodeRow}>
              <span style={{ fontSize:'13px' }}>📍</span>
              <input style={S.pinInput} placeholder="Enter pincode for delivery estimate"
                value={pincode} maxLength={6}
                onChange={(e) => { setPincode(e.target.value.replace(/\D/g,'')); setPinResult(null); }}
                onKeyDown={(e) => e.key === 'Enter' && checkPin()} />
              <button style={S.pinBtn} onClick={checkPin}>CHECK</button>
            </div>
            {pinResult && <p style={{ fontSize:'12px', color:'var(--green)', fontWeight:600, marginTop:'4px' }}>✓ {pinResult}</p>}

            {/* Offers */}
            <div style={S.offersBlock}>
              <p style={{ fontSize:'10px', fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'10px', color:'#666' }}>AVAILABLE OFFERS</p>
              {[
                { icon:'💳', text:<><strong>Bank Offer:</strong> 10% off on HDFC cards. Min. order ₹999</> },
                { icon:'🎁', text:<><strong>BHADRA10:</strong> Extra 10% off on your first order</> },
                { icon:'🚚', text:<><strong>Free Delivery</strong> on orders above ₹799</> },
              ].map((o, i) => (
                <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'9px', marginBottom: i<2 ? '9px' : 0, fontSize:'12px', color:'#555', lineHeight:1.5 }}>
                  <span style={{ fontSize:'13px', flexShrink:0, marginTop:'1px' }}>{o.icon}</span>
                  <span>{o.text}</span>
                </div>
              ))}
            </div>

            {/* Qty + ATC + Buy Now */}
            <div ref={atcRef}>
              <div style={{ display:'flex', gap:'10px', marginBottom:'10px' }}>
                <div style={S.qtyCtrl}>
                  <button style={S.qtyBtn} onClick={() => setQty((q) => Math.max(1, q-1))}>−</button>
                  <span style={{ minWidth:'42px', textAlign:'center', fontSize:'15px', fontWeight:700 }}>{qty}</span>
                  <button style={S.qtyBtn} onClick={() => setQty((q) => Math.min(10, q+1))}>+</button>
                </div>
                <button style={{ ...S.btnATC, ...(addedAnim ? { background:'var(--green)' } : {}) }}
                  onClick={handleATC} disabled={stockLeft === 0}>
                  {addedAnim ? '✓ ADDED TO BAG' : '🛍 ADD TO BAG'}
                </button>
              </div>
              <button style={S.btnBuy} onClick={handleBuyNow} disabled={stockLeft === 0}>
                ⚡ BUY NOW
              </button>
            </div>

            {/* Trust row */}
            <div style={S.trustRow}>
              {[
                { icon:'🚚', label:'FREE SHIPPING', desc:'Orders above ₹799' },
                { icon:'↩',  label:'EASY RETURNS',  desc:'7-day hassle free' },
                { icon:'🔒', label:'SECURE PAY',     desc:'256-bit encrypted' },
              ].map((t) => (
                <div key={t.label} style={S.trustItem}>
                  <div style={{ fontSize:'17px', marginBottom:'3px' }}>{t.icon}</div>
                  <div style={{ fontSize:'9px', fontWeight:700, letterSpacing:'.5px', textTransform:'uppercase', color:'#555' }}>{t.label}</div>
                  <div style={{ fontSize:'9px', color:'#aaa', marginTop:'1px' }}>{t.desc}</div>
                </div>
              ))}
            </div>

            {/* Accordions */}
            {[
              { key:'details', title:'PRODUCT DETAILS', body: Object.keys(specs).length > 0
                ? <table style={{ width:'100%', borderCollapse:'collapse' }}><tbody>
                    {Object.entries(specs).map(([k,v]) => (
                      <tr key={k} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'9px 0', fontSize:'13px', color:'#888', width:'45%', fontWeight:600 }}>{k}</td>
                        <td style={{ padding:'9px 0', fontSize:'13px' }}>{v}</td>
                      </tr>
                    ))}
                  </tbody></table>
                : <p style={{ fontSize:'13px', color:'#555', lineHeight:1.8 }}>{product.description || 'No additional details.'}</p>
              },
              { key:'care', title:'CARE INSTRUCTIONS', body:<p style={{ fontSize:'13px', color:'#555', lineHeight:1.8 }}>Machine wash cold with like colors. Do not bleach. Tumble dry low. Warm iron if needed. Do not dry clean.</p> },
              { key:'ship', title:'SHIPPING & RETURNS',  body:<p style={{ fontSize:'13px', color:'#555', lineHeight:1.8 }}>Free delivery on orders above ₹799. Standard delivery in 4–7 days, express in 2–3 days. 7-day hassle-free returns on unworn items with original tags.</p> },
              { key:'promise', title:'BHADRALOK PROMISE', body:<p style={{ fontSize:'13px', color:'#555', lineHeight:1.8 }}>Every Bhadralok piece is crafted with pride in Bengal. We use ethically sourced fabrics and work directly with Bengali weavers and artisans. Our garments carry the spirit of Bengali cultural heritage.</p> },
            ].map(({ key, title, body }) => (
              <div key={key} style={{ borderTop:'1px solid var(--border)' }}>
                <div style={S.accHdr} onClick={() => toggleAcc(key)}>
                  <span style={{ fontSize:'12px', fontWeight:700, letterSpacing:'1px', textTransform:'uppercase' }}>{title}</span>
                  <span style={{ fontSize:'11px', color:'#888', transition:'transform .25s', display:'inline-block', transform: accOpen[key] ? 'rotate(180deg)' : 'none' }}>▼</span>
                </div>
                {accOpen[key] && <div style={{ paddingBottom:'18px' }}>{body}</div>}
              </div>
            ))}
            <div style={{ borderTop:'1px solid var(--border)' }} />
          </div>
        </div>

        {/* ── Reviews ── */}
        <div style={{ ...S.reviewsSect, padding: isMobile ? '40px 18px' : '72px 60px' }}>
          <div style={{ maxWidth:'1280px', margin:'0 auto' }}>
            {/* Score + bars */}
            <div style={{ ...S.revHeader, gridTemplateColumns: isMobile ? '1fr' : '260px 1fr', gap: isMobile ? '24px' : '48px' }}>
              <div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:'68px', lineHeight:1 }}>4.8</div>
                <div style={{ color:'var(--gold)', fontSize:'24px', letterSpacing:'3px', margin:'4px 0' }}>★★★★★</div>
                <div style={{ fontSize:'13px', color:'#888' }}>{REVIEWS.length} verified reviews</div>
              </div>
              <div style={{ flex:1 }}>
                {[[5,75],[4,15],[3,7],[2,2],[1,1]].map(([star, pct]) => (
                  <div key={star} style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'7px' }}>
                    <span style={{ fontSize:'12px', color:'#888', minWidth:'32px', textAlign:'right' }}>{star}★</span>
                    <div style={{ flex:1, height:'7px', background:'var(--light)', overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${pct}%`, background:'var(--gold)' }} />
                    </div>
                    <span style={{ fontSize:'12px', color:'#888', minWidth:'22px' }}>{pct}%</span>
                  </div>
                ))}
              </div>
            </div>
            <p style={{ fontFamily:'var(--font-display)', fontSize:'28px', letterSpacing:'2px', marginBottom:'24px' }}>CUSTOMER REVIEWS</p>
            <div style={{ ...S.revGrid, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
              {REVIEWS.map((r) => (
                <div key={r.id} style={S.revCard}>
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'10px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                      <div style={{ width:'38px', height:'38px', borderRadius:'50%', background:'var(--black)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontSize:'15px', color:'#fff', flexShrink:0 }}>
                        {r.name[0]}
                      </div>
                      <div>
                        <div style={{ fontSize:'13px', fontWeight:700 }}>{r.name}</div>
                        <div style={{ fontSize:'11px', color:'#888' }}>{r.loc}</div>
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ color:'var(--gold)', fontSize:'13px', letterSpacing:'1px' }}>{'★'.repeat(r.rating)}</div>
                      {r.verified && <div style={{ fontSize:'9px', color:'var(--green)', fontWeight:700, letterSpacing:'1px', marginTop:'2px' }}>✓ VERIFIED</div>}
                    </div>
                  </div>
                  <p style={{ fontSize:'13px', fontWeight:700, margin:'8px 0 5px' }}>{r.title}</p>
                  <p style={{ fontSize:'13px', color:'#555', lineHeight:1.7 }}>{r.text}</p>
                  <div style={{ display:'flex', alignItems:'center', gap:'14px', marginTop:'10px', paddingTop:'10px', borderTop:'1px solid var(--border)' }}>
                    <span style={{ fontSize:'11px', color:'#aaa' }}>{r.date}</span>
                    <span style={{ fontSize:'11px', color:'#888', cursor:'pointer' }}>👍 Helpful ({r.helpful})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Related Products ── */}
        {related.length > 0 && (
          <div style={{ padding: isMobile ? '40px 18px' : '64px 56px', background:'var(--offwhite)' }}>
            <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom: isMobile ? '24px' : '40px' }}>
              <div>
                <p style={{ fontSize:'10px', fontWeight:700, letterSpacing:'3px', textTransform:'uppercase', color:'var(--red)', marginBottom:'9px' }}>ALSO FROM BHADRALOK</p>
                <h2 style={{ fontFamily:'var(--font-display)', fontSize:'clamp(28px,3.5vw,42px)', letterSpacing:'2px', lineHeight:1 }}>YOU MAY ALSO LIKE</h2>
              </div>
              <Link to="/products" style={{ fontSize:'12px', fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--black)', borderBottom:'2px solid var(--red)', paddingBottom:'2px', textDecoration:'none' }}>
                VIEW ALL
              </Link>
            </div>
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: isMobile ? '14px' : '24px' }}>
              {related.map((p) => {
                const rImg  = p.image ? imgSrc(p.image) : null;
                const rDisc = p.compare_price ? Math.round((1 - p.price / p.compare_price) * 100) : 0;
                return (
                  <Link key={p.id} to={`/products/${p.slug}`} style={{ textDecoration:'none', color:'inherit' }}>
                    <div style={{ cursor:'pointer' }}>
                      <div style={{ aspectRatio:'3/4', background:'var(--light)', marginBottom:'13px', position:'relative', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {rImg
                          ? <img src={rImg} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform .4s ease' }} />
                          : <span style={{ fontSize:'40px', opacity:.3 }}>👕</span>}
                        {rDisc > 0 && <span style={{ position:'absolute', top:'9px', left:'9px', background:'var(--red)', color:'#fff', fontSize:'9px', fontWeight:700, letterSpacing:'2px', padding:'3px 9px', textTransform:'uppercase' }}>{rDisc}% OFF</span>}
                      </div>
                      <p style={{ fontFamily:'var(--font-display)', fontSize:'15px', letterSpacing:'.5px', marginBottom:'4px' }}>{p.name}</p>
                      <p style={{ fontSize:'15px', fontWeight:700 }}>
                        ₹{Number(p.price).toLocaleString('en-IN')}
                        {p.compare_price && <span style={{ fontSize:'12px', color:'#aaa', textDecoration:'line-through', marginLeft:'6px' }}>₹{Number(p.compare_price).toLocaleString('en-IN')}</span>}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Sticky Bottom Bar ── */}
      <div style={{ ...S.stickyBar, transform: showBar ? 'translateY(0)' : 'translateY(110%)', padding: isMobile ? '10px 16px' : '12px 60px', gap: isMobile ? '10px' : '20px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
          <div style={{ width:'50px', height:'50px', background:'var(--light)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
            {imgSrc(mainImg)
              ? <img src={imgSrc(mainImg)} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              : <span style={{ fontSize:'20px' }}>👕</span>}
          </div>
          <div>
            <p style={{ fontFamily:'var(--font-display)', fontSize:'18px', letterSpacing:'1px', margin:0 }}>{product.name}</p>
            <p style={{ fontSize:'14px', fontWeight:700, color:'var(--red)', margin:0 }}>₹{Number(product.price).toLocaleString('en-IN')}</p>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          {schema.filter((a) => a.type === 'size' || a.type === 'select').map((attr) => {
            const opts = attr.options?.length ? attr.options : uniqueVals(attr.name);
            const selVal = selections[attr.name] || '';
            return (
              <div key={attr.name} style={{ display:'flex', gap:'5px' }}>
                {opts.map((opt) => (
                  <button key={opt}
                    style={{ padding:'6px 12px', border: selVal===opt ? '1.5px solid var(--black)' : '1.5px solid rgba(0,0,0,.15)', fontSize:'11px', fontWeight:700, cursor:'pointer', background: selVal===opt ? 'var(--black)' : '#fff', color: selVal===opt ? '#fff' : 'inherit', transition:'all .2s' }}
                    onClick={() => selectVal(attr.name, opt)}>
                    {opt}
                  </button>
                ))}
              </div>
            );
          })}
          <button style={S.sbATC} onClick={handleATC}>{addedAnim ? '✓ ADDED' : 'ADD TO BAG'}</button>
        </div>
      </div>

      <style>{`@keyframes livePulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
    </>
  );
}

const S = {
  loadWrap:    { display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', marginTop:'calc(var(--ticker-h) + var(--nav-h))' },
  spinner:     { width:'36px', height:'36px', border:'3px solid var(--border)', borderTopColor:'var(--red)', borderRadius:'50%', animation:'spin 0.7s linear infinite' },

  bc:          { padding:'18px 60px', display:'flex', alignItems:'center', gap:'10px', fontSize:'12px', color:'#888' },
  bcLink:      { textDecoration:'none', color:'#888' },
  bcSep:       { color:'#ccc' },
  bcCur:       { color:'var(--black)', fontWeight:600 },

  pdpWrap:     { display:'grid', gridTemplateColumns:'1fr 1fr', maxWidth:'1400px', margin:'0 auto', padding:'0 60px' },

  /* Gallery */
  galleryWrap: { position:'sticky', top:'calc(var(--ticker-h) + var(--nav-h))', height:'calc(100vh - var(--ticker-h) - var(--nav-h))', display:'flex', gap:'10px', padding:'20px 0' },
  thumbsRail:  { display:'flex', flexDirection:'column', gap:'8px', width:'70px', flexShrink:0, overflowY:'auto', scrollbarWidth:'none' },
  thumb:       { width:'70px', height:'70px', background:'var(--light)', cursor:'pointer', border:'2px solid transparent', display:'flex', alignItems:'center', justifyContent:'center', transition:'all .2s', flexShrink:0, overflow:'hidden' },
  gMain:       { flex:1, background:'var(--light)', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', overflow:'hidden', cursor:'zoom-in' },
  gTag:        { position:'absolute', top:'14px', left:'14px', background:'var(--red)', color:'#fff', fontSize:'9px', fontWeight:700, letterSpacing:'2px', padding:'5px 14px', textTransform:'uppercase', zIndex:2 },
  gArrows:     { position:'absolute', bottom:'14px', right:'14px', display:'flex', gap:'7px', zIndex:2 },
  gArrow:      { width:'34px', height:'34px', background:'#fff', border:'none', cursor:'pointer', fontSize:'18px', display:'flex', alignItems:'center', justifyContent:'center', transition:'all .2s' },

  /* Info */
  infoPanel:   { padding:'24px 0 80px 44px', overflowY:'auto', scrollbarWidth:'none', maxHeight:'calc(100vh - var(--ticker-h) - var(--nav-h))' },
  pdpName:     { fontFamily:'var(--font-display)', fontSize:'clamp(28px,3vw,46px)', letterSpacing:'2px', lineHeight:1, marginBottom:'8px' },
  ratingRow:   { display:'flex', alignItems:'center', gap:'10px', padding:'12px 0', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', marginBottom:'18px', flexWrap:'wrap' },
  liveDot:     { width:'7px', height:'7px', borderRadius:'50%', background:'var(--green)', display:'inline-block', animation:'livePulse 1.5s infinite' },

  pickerLabel: { fontSize:'11px', fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', color:'#888', marginBottom:'9px', display:'flex', alignItems:'center', gap:'8px', margin:0 },
  cOpt:        { width:'38px', height:'38px', borderRadius:'50%', border:'3px solid transparent', cursor:'pointer', transition:'all .2s', flexShrink:0 },
  sgLink:      { background:'none', border:'none', fontSize:'12px', color:'var(--red)', fontWeight:600, cursor:'pointer', textDecoration:'underline', padding:0 },
  szOpt:       { minWidth:'50px', padding:'10px 14px', border:'1.5px solid rgba(0,0,0,.15)', background:'#fff', fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:600, cursor:'pointer', transition:'all .2s', textAlign:'center' },
  szOptSel:    { background:'var(--black)', borderColor:'var(--black)', color:'#fff' },
  szOptOOS:    { opacity:.3, cursor:'not-allowed', textDecoration:'line-through' },
  stockDot:    { width:'8px', height:'8px', borderRadius:'50%', display:'inline-block', flexShrink:0 },

  pincodeRow:  { display:'flex', alignItems:'center', gap:'8px', padding:'12px 0', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', marginBottom:'18px' },
  pinInput:    { flex:1, border:'none', background:'transparent', fontFamily:'var(--font-body)', fontSize:'13px', outline:'none' },
  pinBtn:      { background:'none', border:'none', color:'var(--red)', fontSize:'12px', fontWeight:700, cursor:'pointer', letterSpacing:'.5px' },

  offersBlock: { background:'#fff', border:'1px solid var(--border)', borderLeft:'3px solid var(--gold)', padding:'14px 18px', marginBottom:'18px' },

  qtyCtrl:     { display:'flex', alignItems:'center', border:'1.5px solid rgba(0,0,0,.15)', background:'#fff' },
  qtyBtn:      { width:'40px', height:'52px', border:'none', background:'none', fontSize:'18px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'background .2s' },
  btnATC:      { flex:1, background:'var(--black)', color:'#fff', border:'none', height:'52px', padding:'0 24px', fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', cursor:'pointer', transition:'all .2s', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px' },
  btnBuy:      { width:'100%', background:'var(--red)', color:'#fff', border:'none', height:'48px', padding:'0 24px', fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:700, letterSpacing:'2px', textTransform:'uppercase', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', marginBottom:'14px', transition:'background .2s' },

  trustRow:    { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1px', background:'var(--border)', border:'1px solid var(--border)', marginBottom:'22px' },
  trustItem:   { background:'#fff', padding:'12px', textAlign:'center' },

  accHdr:      { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 0', cursor:'pointer', userSelect:'none' },
  pb:          { fontSize:'10px', fontWeight:700, letterSpacing:'2px', padding:'5px 14px', textTransform:'uppercase' },

  /* Size Guide */
  sgBack:      { position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center' },
  sgModal:     { background:'#fff', padding:'40px', maxWidth:'520px', width:'90vw', position:'relative', maxHeight:'90vh', overflowY:'auto' },
  sgClose:     { position:'absolute', top:'16px', right:'16px', background:'none', border:'none', fontSize:'18px', cursor:'pointer', color:'#666' },
  sgTitle:     { fontFamily:'var(--font-display)', fontSize:'26px', letterSpacing:'3px', marginBottom:'16px' },
  sgTable:     { width:'100%', borderCollapse:'collapse', marginBottom:'4px' },
  sgTh:        { padding:'10px 12px', background:'var(--black)', color:'#fff', fontSize:'11px', fontWeight:700, letterSpacing:'1px', textAlign:'left' },
  sgTd:        { padding:'9px 12px', borderBottom:'1px solid var(--border)', fontSize:'13px' },

  /* Zoom */
  zoomOv:      { position:'fixed', inset:0, background:'rgba(0,0,0,.85)', zIndex:350, display:'flex', alignItems:'center', justifyContent:'center' },
  zoomClose:   { position:'absolute', top:'20px', right:'20px', background:'none', border:'none', color:'#fff', fontSize:'22px', cursor:'pointer', zIndex:1 },

  /* Reviews */
  reviewsSect: { background:'#fff', padding:'72px 60px' },
  revHeader:   { display:'grid', gridTemplateColumns:'260px 1fr', gap:'48px', alignItems:'start', marginBottom:'40px', paddingBottom:'40px', borderBottom:'1px solid var(--border)' },
  revGrid:     { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px' },
  revCard:     { background:'var(--offwhite)', padding:'22px', borderBottom:'3px solid var(--red)' },

  /* Sticky bar */
  stickyBar:   { position:'fixed', bottom:0, left:0, right:0, background:'#fff', borderTop:'2px solid var(--red)', zIndex:150, transition:'transform .35s cubic-bezier(.4,0,.2,1)', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 60px', gap:'20px', boxShadow:'0 -4px 20px rgba(0,0,0,.08)' },
  sbATC:       { background:'var(--red)', color:'#fff', border:'none', padding:'11px 28px', fontFamily:'var(--font-body)', fontSize:'12px', fontWeight:700, letterSpacing:'1.5px', textTransform:'uppercase', cursor:'pointer', whiteSpace:'nowrap', transition:'background .2s' },
};
